import { ModusWcSliderCustomEvent } from '@trimble-oss/moduswebcomponents';
import {
  Component,
  createEffect,
  createMemo,
  createSignal,
  For,
  mergeProps,
  on,
  Show,
} from 'solid-js';
import {
  FeetInchUnit,
  PointCloudStylingBand,
  PointCloudStylingUOM,
} from '@trimble/types';
import { PointCloudUOMInputControl } from '../point-cloud-uom-input/point-cloud-uom-input';
import { getPointCloudElevationGradient } from '@trimble/utilities';
import {
  POINT_CLOUD_ELEVATION_DEFAULTS,
  PointCloudElevationControlProps,
} from './point-cloud-elevation.model';
import { BtnClasses, IconButton } from '@tcweb/connect-ui';
import { fixModusSliderStyle } from '@trimble/ui-components/solidjs';
import { convertMetersToFeetInches } from '@trimble/utilities';
import { t } from '@trimble/i18n';

const BAND_COUNT_CONFIG = {
  min: 1,
  max: 9,
  step: 1,
} as const;

const DEFAULT_LENGTH_FRACTIONS = 16;
const RADIX = 10;
const PERCENTAGE = 100;

// Elevation bands are structured in pairs (0&1, 2&3, etc.) with matching colors for solid appearance.
// Bands 0&1 represent "out of tolerance" areas, so the first tolerance triangle
// appears at index 2, marking the start of the actual tolerance bands.
const FIRST_TOLERANCE_TRIANGLE_INDEX = 2;

// Triangle marker component
const TriangleMarker: Component = () => (
  <div
    class="w-0 h-0"
    style={{
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'border-left': '4px solid transparent',
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'border-right': '4px solid transparent',
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'border-bottom': '6px solid #374151',
    }}
  />
);

export const PointCloudElevationControl: Component<
  PointCloudElevationControlProps
> = (props: PointCloudElevationControlProps) => {
  fixModusSliderStyle();

  const activeProps = mergeProps(POINT_CLOUD_ELEVATION_DEFAULTS, props);

  const [reference, setReference] = createSignal<number | undefined>(undefined);
  const [tolerance, setTolerance] = createSignal<number>(0);
  const [offset, setOffset] = createSignal<number>(0);
  const [bandCount, setBandCount] = createSignal<number>(0);
  const [elevationPick, setElevationPick] = createSignal<boolean>(false);
  const [gradient, setGradient] = createSignal<PointCloudStylingBand[]>([]);

  const middleToleranceTriangleIndex = createMemo(() => {
    // eslint-disable-next-line no-magic-numbers
    return Math.floor(gradient().length / 2);
  });

  const lastToleranceTriangleIndex = createMemo(() => {
    // eslint-disable-next-line no-magic-numbers
    return gradient().length - 2;
  });

  createEffect(() => {
    setReference(activeProps.reference);
  });

  createEffect(() => {
    setTolerance(activeProps.tolerance);
  });

  createEffect(() => {
    setOffset(activeProps.offset);
  });

  createEffect(() => {
    setBandCount(activeProps.bandCount);
  });

  createEffect(
    on(
      () => props.elevationPicked,
      (elevationPicked) => {
        if (!elevationPick()) {
          return;
        }

        setReference(elevationPicked);

        // Disable picking after a successful pick
        setElevationPick(false);
        props.onElevationPickingToggle?.(false);
      }
    )
  );

  createEffect(() => {
    setElevationPick(activeProps.elevationPicking);
  });

  createEffect(() => {
    const bandCountValue = bandCount();
    setGradient(getGradient(bandCountValue));
  });

  const formatValueWithUnit = (
    value: number,
    uom: PointCloudStylingUOM
  ): string => {
    if (uom.feetInches) {
      const { feet, inches, fraction } = convertMetersToFeetInches(
        value,
        uom.lengthFractions ?? DEFAULT_LENGTH_FRACTIONS
      );

      return `${feet.toString()} ft ${inches.toString()} ${fraction.toString()}/${(uom.lengthFractions ?? 0).toString()} in`;
    } else {
      const convertedValue = value * uom.meterToUnitFactor;
      const formattedValue = convertedValue.toFixed(uom.lengthDecimals ?? 0);
      return `${formattedValue} ${uom.unitLabel}`;
    }
  };

  const toleranceText = createMemo(() => {
    const uom = props.uom;
    return formatValueWithUnit(tolerance(), uom);
  });

  const referenceText = createMemo(() => {
    const uom = props.uom;
    const value = (reference() ?? 0) + offset();
    return formatValueWithUnit(value, uom);
  });

  const handleBandCountChange = (
    event: ModusWcSliderCustomEvent<InputEvent>
  ): void => {
    const count = parseInt(
      (event.detail.target as HTMLInputElement).value,
      RADIX
    );

    setBandCount(count);
    props.onBandCountChange?.(count);
  };

  const toggleElevationPick = (): void => {
    const isElevationPickEnabled = elevationPick();
    setElevationPick(!isElevationPickEnabled);
    props.onElevationPickingToggle?.(!isElevationPickEnabled);
  };

  const uomSymbol = createMemo((): string => {
    return props.uom.feetInches ? FeetInchUnit : props.uom.unitLabel;
  });

  function getGradient(bandCount: number): { start: number; color: string }[] {
    return getPointCloudElevationGradient(bandCount + 1).map(
      ([value, color]) => ({
        start: value,
        color,
      })
    );
  }

  function handleReferenceChange(value: number): void {
    setReference(value);
    props.onReferenceChange?.(value);
  }

  function handleToleranceChange(value: number): void {
    setTolerance(value);
    props.onToleranceChange?.(value);
  }

  function handleOffsetChange(value: number): void {
    setOffset(value);
    props.onOffsetChange?.(value);
  }

  return (
    <div class="w-full flex flex-col gap-4">
      {/* Reference */}
      <div class="flex flex-col gap-1">
        <span id="elevation-reference-label">
          {t('mobileMapping.styles.elevation.reference')}
          <span>&nbsp;({uomSymbol()})</span>
        </span>
        <div class="flex flex-row gap-2">
          <PointCloudUOMInputControl
            showUomLabel={false}
            aria-labelledby="elevation-reference-label"
            valueInMeters={reference() ?? 0}
            uom={props.uom}
            onCommit={handleReferenceChange}
          />

          <IconButton
            data-cy="elevation-pick-button"
            color={elevationPick() ? BtnClasses.PRIMARY : BtnClasses.TERTIARY}
            title={
              elevationPick()
                ? t('mobileMapping.styles.elevation.stopPicking')
                : t('mobileMapping.styles.elevation.startPicking')
            }
            icon="location"
            size="icon-medium"
            clicked={toggleElevationPick}
          />
        </div>
      </div>

      {/* Tolerance */}
      <div class="flex flex-col gap-1">
        <span id="elevation-tolerance-label">
          {t('mobileMapping.styles.elevation.tolerance')}
          <span>&nbsp;({uomSymbol()})</span>
        </span>
        <PointCloudUOMInputControl
          showUomLabel={false}
          aria-labelledby="elevation-tolerance-label"
          valueInMeters={tolerance()}
          uom={props.uom}
          onCommit={handleToleranceChange}
        />
      </div>

      {/* Offset */}
      <div class="flex flex-col gap-1">
        <span id="elevation-offset-label">
          {t('mobileMapping.styles.elevation.offset')}
          <span>&nbsp;({uomSymbol()})</span>
        </span>
        <PointCloudUOMInputControl
          showUomLabel={false}
          aria-labelledby="elevation-offset-label"
          valueInMeters={offset()}
          uom={props.uom}
          onCommit={handleOffsetChange}
        />
      </div>

      {/* Band Count */}
      <div class="flex flex-row gap-1">
        <span class="flex flex-1" id="elevation-band-count-label">
          {t('mobileMapping.styles.elevation.bandCount', {
            count: bandCount().toString(),
          })}
        </span>
        <modus-wc-slider
          aria-labelledby="elevation-band-count-label"
          data-testid="point-cloud-elevation-band-count-slider"
          value={bandCount()}
          min={BAND_COUNT_CONFIG.min}
          max={BAND_COUNT_CONFIG.max}
          step={BAND_COUNT_CONFIG.step}
          class="flex items-center"
          on:inputChange={handleBandCountChange}
        />
      </div>

      {/* Legend */}
      <div class="flex flex-col gap-1">
        <span class="font-semibold">
          {t('mobileMapping.styles.elevation.legend')}
        </span>

        <div class="flex justify-between text-xs">
          <span>{t('mobileMapping.styles.elevation.below')}</span>
          <span>{t('mobileMapping.styles.elevation.reference')}</span>
          <span>{t('mobileMapping.styles.elevation.above')}</span>
        </div>

        <div class="flex flex-col gap-2">
          {/* Triangle markers */}
          <div class="relative">
            <div class="border border-gray-300 rounded-sm overflow-hidden h-5">
              <div class="flex h-full w-full">
                <For each={gradient()}>
                  {(band) => (
                    <div
                      data-testid="point-cloud-elevation-legend-item"
                      class="h-full min-w-[1px] grow"
                      style={{
                        // eslint-disable-next-line @typescript-eslint/naming-convention
                        'background-color': band.color,
                      }}
                    />
                  )}
                </For>

                <div class="absolute top-full left-0 w-full">
                  <For each={gradient()}>
                    {(band, index) => (
                      <div
                        class="absolute top-0 h-full"
                        style={{
                          left: `${((index() / gradient().length) * PERCENTAGE).toString()}%`,
                          width: `${(PERCENTAGE / gradient().length).toString()}%`,
                          // eslint-disable-next-line @typescript-eslint/naming-convention
                          'background-color': band.color,
                        }}
                      >
                        {/* First tolerance triangle */}
                        <Show when={index() === FIRST_TOLERANCE_TRIANGLE_INDEX}>
                          <div class="flex flex-col -ml-1">
                            <TriangleMarker />
                          </div>
                        </Show>

                        {/* Middle reference triangle - around the center/black strip area */}
                        <Show when={index() === middleToleranceTriangleIndex()}>
                          <div class="flex flex-col -ml-1">
                            <TriangleMarker />
                          </div>
                        </Show>

                        {/* Last tolerance triangle */}
                        <Show when={index() === lastToleranceTriangleIndex()}>
                          <div class="flex flex-col -ml-1">
                            <TriangleMarker />
                          </div>
                        </Show>
                      </div>
                    )}
                  </For>
                </div>
              </div>
            </div>
          </div>

          <div class="flex justify-between text-xs">
            <span>-{toleranceText()}</span>
            <span>{referenceText()}</span>
            <span>+{toleranceText()}</span>
          </div>
        </div>
      </div>
    </div>
  );
};
